// Check-before-add prescribing guard for one-tap drug cards.
// Returns [{ severity: "block"|"warn", text }] — block requires explicit override.
import { sameClassAlerts } from "./drugClasses.js";
import { ceilingCheck, ceilingAudit, pediatricCheck } from "./doseCeilings.js";
import { pregnancyRefLookup } from "./pregnancyRef.js";

const NSAID_RE = /(diclofenac|aceclofenac|ibuprofen|naproxen|etoricoxib|ketorolac|mefenamic|piroxicam|indomethacin|nimesulide)/;

// QT-prolonging drugs — stacking multiple raises torsades risk
const QT_RE = /(azithromycin|levofloxacin|moxifloxacin|ondansetron|domperidone|escitalopram|citalopram|amitriptyline|hydroxyzine|amiodarone|sotalol|hydroxychloroquine|chloroquine|haloperidol|fluconazole)/;

// AV-nodal / rate-limiting drugs — dangerous in conduction disease without a pacemaker
const AV_NODAL_RE = /(metoprolol|atenolol|bisoprolol|nebivolol|propranolol|carvedilol|labetalol|verapamil|diltiazem|digoxin|ivabradine)/;

const drugText = (rx) => `${rx?.generic_name || ""} ${rx?.composition || ""} ${rx?.brand_name || ""}`.toLowerCase();
const isTopical = (rx) => /topical|gel|cream|ointment|nasal|eye|ear|spray/i.test(`${rx?.route || ""} ${rx?.dosage_form || ""}`);

// One-tap SAFE ALTERNATIVES offered inside the block dialog
const ALT = {
  paracetamol: { label: "Paracetamol 650 TDS (safe analgesic)", rx: { brand_name: "Dolo 650", generic_name: "Paracetamol", composition: "Paracetamol 650 mg", strength: "650 mg", dose: "1 tab", route: "Oral", frequency: "TDS", timing: "After food", duration: "5 days" } },
  linagliptin: { label: "Linagliptin 5 OD (no renal adjustment needed)", rx: { brand_name: "Trajenta 5", generic_name: "Linagliptin", strength: "5 mg", dose: "1 tab", route: "Oral", frequency: "OD" } },
  empagliflozin: { label: "Empagliflozin 10 OD (HF benefit)", rx: { brand_name: "Jardiance 10", generic_name: "Empagliflozin", strength: "10 mg", dose: "1 tab", route: "Oral", frequency: "OD", timing: "Morning" } },
  cefixime: { label: "Cefixime 200 BD (renal-safe UTI option)", rx: { brand_name: "Taxim-O 200", generic_name: "Cefixime", strength: "200 mg", dose: "1 tab", route: "Oral", frequency: "BD", duration: "7 days" } },
  domperidone: { label: "Domperidone 10 TDS (Parkinson's-safe)", rx: { brand_name: "Domstal 10", generic_name: "Domperidone", strength: "10 mg", dose: "1 tab", route: "Oral", frequency: "TDS", timing: "Before food" } },
  mirabegron: { label: "Mirabegron 25 OD (glaucoma-safe for OAB)", rx: { brand_name: "Mirago 25", generic_name: "Mirabegron", strength: "25 mg", dose: "1 tab", route: "Oral", frequency: "OD" } },
  bilastine: { label: "Bilastine 20 OD (non-anticholinergic antihistamine)", rx: { brand_name: "Bilaxten 20", generic_name: "Bilastine", strength: "20 mg", dose: "1 tab", route: "Oral", frequency: "OD", timing: "Empty stomach" } },
  bisoprolol: { label: "Bisoprolol 2.5 OD (cardioselective — cautious)", rx: { brand_name: "Concor 2.5", generic_name: "Bisoprolol", strength: "2.5 mg", dose: "1 tab", route: "Oral", frequency: "OD" } },
  labetalol: { label: "Labetalol 100 BD (pregnancy-safe BP control)", rx: { brand_name: "Labebet 100", generic_name: "Labetalol", strength: "100 mg", dose: "1 tab", route: "Oral", frequency: "BD" } },
};

const RENAL_RULES = [
  { re: /metformin/, block: 30, warn: 45, warnText: "eGFR 30-44 — halve metformin (max 1000 mg/day)", blockText: "Metformin is contraindicated at eGFR < 30", alt: "linagliptin" },
  { re: /gliflozin/, block: 20, warn: 25, warnText: "SGLT2i below eGFR 25 — do not initiate for glycaemic control (renal/HF indication is specialist territory)", blockText: "SGLT2i at eGFR < 20 — avoid initiation", alt: "linagliptin" },
  { re: NSAID_RE, block: 30, warn: 60, warnText: "NSAID with eGFR < 60 — shortest course, recheck creatinine; prefer paracetamol/topical", blockText: "NSAID at eGFR < 30 — avoid (AKI risk); use paracetamol/topical", skipTopical: true, alt: "paracetamol" },
  { re: /nitrofurantoin/, block: 45, blockText: "Nitrofurantoin ineffective & toxic below eGFR 45 — use alternative", alt: "cefixime" },
  { re: /allopurinol/, warn: 60, warnText: "Allopurinol in CKD — start 50-100 mg and titrate slowly" },
  { re: /(rivaroxaban|apixaban|dabigatran)/, block: 15, warn: 30, warnText: "DOAC with eGFR < 30 — dose reduction/avoidance per agent; verify", blockText: "DOAC at eGFR < 15 — avoid" },
  { re: /tramadol/, warn: 30, warnText: "Tramadol at eGFR < 30 — extend interval to q12h (max 200 mg/day)" },
  { re: /(glibenclamide|glyburide)/, warn: 60, warnText: "Glibenclamide in CKD — high hypoglycaemia risk; prefer gliclazide/DPP4i" },
  { re: /sitagliptin/, warn: 45, warnText: "Sitagliptin — reduce to 50 mg (eGFR 30-45) or 25 mg (< 30)" },
  { re: /(spironolactone|eplerenone)/, block: 30, blockText: "MRA at eGFR < 30 — hyperkalaemia risk, avoid" },
  { re: /(alendronate|risedronate|zoledronic)/, block: 35, blockText: "Oral/IV bisphosphonate contraindicated below eGFR 35" },
  { re: /digoxin/, warn: 60, warnText: "Digoxin in CKD — reduce dose and monitor levels" },
];

const AGE_RULES = [
  { re: /(chlorpheniramine|diphenhydramine|hydroxyzine|promethazine|pheniramine|cyproheptadine)/, text: "Beers ≥65: sedating antihistamine — falls/confusion risk; prefer bilastine/fexofenadine" },
  { re: /(glibenclamide|glyburide)/, text: "Beers ≥65: glibenclamide — prolonged hypoglycaemia; prefer gliclazide MR" },
  { re: /(alprazolam|diazepam|lorazepam|clonazepam|nitrazepam)/, text: "Beers ≥65: benzodiazepine — falls/cognitive risk; use lowest dose, shortest time" },
  { re: /zolpidem/, text: "Beers ≥65: zolpidem — falls/complex sleep behaviour risk" },
  { re: /(amitriptyline|imipramine)/, text: "Beers ≥65: TCA — anticholinergic burden; prefer duloxetine/pregabalin for neuropathic pain" },
  { re: /oxybutynin/, text: "Beers ≥65: oxybutynin — cognitive decline risk; prefer mirabegron" },
  { re: NSAID_RE, skipTopical: true, text: "STOPP ≥65: NSAID — GI bleed + AKI risk; lowest dose, add PPI, prefer paracetamol/topical" },
  { re: /(ketorolac|indomethacin)/, text: "STOPP ≥65: ketorolac/indomethacin — highest-risk NSAIDs in elderly; avoid" },
  { re: /metoclopramide/, text: "STOPP ≥65: metoclopramide — extrapyramidal effects; prefer domperidone" },
  { re: /(cyclobenzaprine|chlorzoxazone|carisoprodol|thiocolchicoside|methocarbamol)/, text: "Beers ≥65: muscle relaxant — sedation/falls with little benefit" },
  { re: /(prazosin|terazosin)/, text: "STOPP ≥65: alpha-blocker — orthostatic hypotension/falls; prefer tamsulosin for BPH" },
  { re: /digoxin/, text: "STOPP ≥65: keep digoxin ≤ 125 mcg/day; verify levels + RFT" },
  { re: /nitrofurantoin/, text: "Beers ≥65: nitrofurantoin — confirm eGFR > 45 before use" },
  { re: /theophylline/, text: "STOPP ≥65: theophylline — narrow therapeutic index; prefer inhaled therapy" },
  { re: /clonidine/, text: "Beers ≥65: clonidine — orthostatic hypotension/bradycardia risk" },
  { re: /(haloperidol|risperidone|olanzapine|quetiapine)/, text: "Beers ≥65: antipsychotic — stroke/mortality risk in dementia; lowest dose, shortest time" },
];

const ALLERGY_GROUPS = [
  { tokenRe: /(penicillin|amoxicillin|amoxyclav|augmentin)/, drugRe: /(penicillin|amoxicillin|ampicillin|amoxyclav|augmentin|piperacillin|cloxacillin)/, label: "Penicillin-group allergy on record" },
  { tokenRe: /(sulfa|sulpha|cotrimoxazole)/, drugRe: /(sulfamethoxazole|cotrimoxazole|sulfasalazine|sulfadiazine)/, label: "Sulfa allergy on record" },
  { tokenRe: /(nsaid|aspirin|ibuprofen|diclofenac)/, drugRe: new RegExp(NSAID_RE.source + "|aspirin"), label: "NSAID/Aspirin allergy on record" },
];

// Condition (Known Case / diagnosis) → drug contraindications
const CONDITION_RULES = [
  { cond: /heart failure|hfref|hfpef|cardiomyopathy/, re: /pioglitazone/, severity: "block", text: "Pioglitazone in heart failure — fluid retention, contraindicated", alt: "empagliflozin" },
  { cond: /heart failure|hfref|hfpef/, re: NSAID_RE, severity: "block", text: "NSAID in heart failure — fluid retention/decompensation risk", skipTopical: true, alt: "paracetamol" },
  { cond: /heart failure|hfref/, re: /(verapamil|diltiazem)/, severity: "warn", text: "Non-DHP CCB in HFrEF — negative inotropy, avoid" },
  { cond: /heart failure/, re: /saxagliptin/, severity: "warn", text: "Saxagliptin — HF-hospitalisation signal (SAVOR-TIMI); prefer sitagliptin/linagliptin" },
  { cond: /asthma/, re: /(propranolol|carvedilol|sotalol)/, severity: "block", text: "Non-selective beta-blocker in asthma — bronchospasm", alt: "bisoprolol" },
  { cond: /asthma/, re: /(metoprolol|atenolol|bisoprolol|nebivolol)/, severity: "warn", text: "Beta-blocker in asthma — even cardioselective ones need caution; lowest dose, monitor" },
  { cond: /asthma|nasal polyp/, re: new RegExp(NSAID_RE.source + "|aspirin"), severity: "warn", text: "NSAID/aspirin in asthma — AERD risk; ask about prior aspirin reactions", skipTopical: true },
  { cond: /cirrhosis|alcoholic liver/, re: NSAID_RE, severity: "block", text: "NSAID in cirrhosis/ALD — variceal bleeding + hepatorenal risk", skipTopical: true, alt: "paracetamol" },
  { cond: /cirrhosis/, re: /(alprazolam|diazepam|lorazepam|clonazepam|zolpidem)/, severity: "warn", text: "Sedative in cirrhosis — can precipitate hepatic encephalopathy" },
  { cond: /cirrhosis|alcoholic liver|\bmash\b/, re: /(methotrexate|nimesulide)/, severity: "block", text: "Hepatotoxic drug with existing liver disease — avoid" },
  { cond: /epilepsy/, re: /tramadol/, severity: "warn", text: "Tramadol in epilepsy — lowers seizure threshold" },
  { cond: /epilepsy/, re: /(levofloxacin|ciprofloxacin|ofloxacin|moxifloxacin)/, severity: "warn", text: "Fluoroquinolone in epilepsy — seizure threshold effect" },
  { cond: /epilepsy/, re: /bupropion/, severity: "block", text: "Bupropion in epilepsy — contraindicated" },
  { cond: /ckd stage 4|ckd stage 5|on dialysis/, re: /metformin/, severity: "block", text: "Metformin in CKD 4-5/dialysis — contraindicated (lactic acidosis)", alt: "linagliptin" },
  { cond: /ckd stage 3|ckd stage 4|ckd stage 5|on dialysis/, re: NSAID_RE, severity: "block", text: "NSAID in CKD — AKI/progression risk; use paracetamol/topical", skipTopical: true, alt: "paracetamol" },
  { cond: /\bgout\b|hyperuricaemia/, re: /(hydrochlorothiazide|chlorthalidone|indapamide)/, severity: "warn", text: "Thiazide raises urate — prefer losartan/amlodipine in gout" },
  { cond: /glaucoma/, re: /(oxybutynin|solifenacin)/, severity: "block", text: "Anticholinergic with glaucoma — angle-closure risk", alt: "mirabegron" },
  { cond: /glaucoma/, re: /(chlorpheniramine|hydroxyzine|promethazine)/, severity: "block", text: "Sedating antihistamine with glaucoma — angle-closure risk", alt: "bilastine" },
  { cond: /glaucoma/, re: /(amitriptyline|imipramine)/, severity: "block", text: "TCA with glaucoma — angle-closure risk" },
  { cond: /glaucoma/, re: /(prednisolone|dexamethasone|methylprednisolone|betamethasone|deflazacort)/, severity: "warn", text: "Systemic steroid with glaucoma — raises IOP; coordinate with ophthalmologist" },
  { cond: /peptic ulcer|gi bleed/, re: new RegExp(NSAID_RE.source + "|aspirin"), severity: "warn", text: "NSAID/aspirin with peptic ulcer history — co-prescribe PPI or use alternative", skipTopical: true },
  { cond: /parkinson/, re: /(metoclopramide|prochlorperazine|flunarizine|levosulpiride)/, severity: "block", text: "Dopamine blocker in Parkinson's — worsens parkinsonism (use domperidone)", alt: "domperidone" },
  { cond: /dementia/, re: /(oxybutynin|amitriptyline|chlorpheniramine|hydroxyzine|promethazine)/, severity: "warn", text: "Anticholinergic in dementia — accelerates cognitive decline" },
  { cond: /osteoporosis/, re: /(prednisolone|dexamethasone|methylprednisolone|deflazacort)/, severity: "warn", text: "Steroid with osteoporosis — bone loss; keep course short, ensure Ca/D3" },
  // ── Lactation safety (flag or "breastfeeding" in known/diagnosis) ──
  { cond: /lactat|breastfeed/, re: /(methotrexate|mycophenolate|cyclophosphamide|isotretinoin)/, severity: "block", text: "Cytotoxic/retinoid while breastfeeding — contraindicated" },
  { cond: /lactat|breastfeed/, re: /amiodarone/, severity: "block", text: "Amiodarone in lactation — iodine load harms infant thyroid" },
  { cond: /lactat|breastfeed/, re: /(ergotamine|dihydroergotamine)/, severity: "block", text: "Ergot alkaloid in lactation — infant ergotism + suppresses milk" },
  { cond: /lactat|breastfeed/, re: /lithium/, severity: "warn", text: "Lithium in lactation — significant infant levels; specialist monitoring needed" },
  { cond: /lactat|breastfeed/, re: /(codeine|tramadol)/, severity: "warn", text: "Opioid in lactation — infant sedation/respiratory depression (CYP2D6); prefer paracetamol/ibuprofen", alt: "paracetamol" },
  { cond: /lactat|breastfeed/, re: /(aspirin|ecosprin)/, severity: "warn", text: "Analgesic-dose aspirin in lactation — Reye's risk via milk; low antiplatelet dose OK; prefer paracetamol/ibuprofen" },
  { cond: /lactat|breastfeed/, re: /nimesulide/, severity: "warn", text: "Nimesulide in lactation — avoid; ibuprofen is the compatible NSAID" },
  { cond: /lactat|breastfeed/, re: /(doxycycline|tetracycline|minocycline)/, severity: "warn", text: "Tetracycline in lactation — short course acceptable, avoid prolonged use" },
  { cond: /lactat|breastfeed/, re: /floxacin/, severity: "warn", text: "Fluoroquinolone in lactation — caution; prefer amoxicillin/cephalosporin/azithromycin" },
  { cond: /lactat|breastfeed/, re: /(ethinyl\s?estradiol|combined oral contraceptive)/, severity: "warn", text: "Estrogen OCP suppresses milk supply — prefer progestin-only pill" },
  { cond: /lactat|breastfeed/, re: /pseudoephedrine/, severity: "warn", text: "Pseudoephedrine reduces milk supply — avoid if maintaining lactation" },
  { cond: /lactat|breastfeed/, re: /(alprazolam|diazepam|clonazepam)/, severity: "warn", text: "Long-acting benzodiazepine in lactation — infant sedation; shortest course, prefer lorazepam" },
  { cond: /lactat|breastfeed/, re: /(cabergoline|bromocriptine)/, severity: "warn", text: "Dopamine agonist suppresses lactation — confirm this is intended" },
  // ── G6PD deficiency (flag or in known/diagnosis) ──
  { cond: /g6pd/, re: /(primaquine|tafenoquine)/, severity: "block", text: "Primaquine/tafenoquine in G6PD deficiency — acute haemolysis; contraindicated" },
  { cond: /g6pd/, re: /dapsone/, severity: "block", text: "Dapsone in G6PD deficiency — haemolysis; contraindicated" },
  { cond: /g6pd/, re: /nitrofurantoin/, severity: "block", text: "Nitrofurantoin in G6PD deficiency — haemolytic anaemia; use alternative", alt: "cefixime" },
  { cond: /g6pd/, re: /(cotrimoxazole|sulfamethoxazole|sulfasalazine|sulfadiazine)/, severity: "block", text: "Sulfonamide in G6PD deficiency — haemolysis risk; avoid" },
  { cond: /g6pd/, re: /(methylene blue|rasburicase)/, severity: "block", text: "Contraindicated in G6PD deficiency — severe haemolysis" },
  { cond: /g6pd/, re: /(ciprofloxacin|ofloxacin|norfloxacin|nalidixic)/, severity: "warn", text: "Quinolone in G6PD — haemolysis reported; use cautiously" },
  { cond: /g6pd/, re: /(chloroquine|hydroxychloroquine|quinine)/, severity: "warn", text: "Antimalarial in G6PD — haemolysis risk; monitor Hb" },
  { cond: /g6pd/, re: /(aspirin|ecosprin)/, severity: "warn", text: "High-dose aspirin in G6PD — haemolysis risk; low dose usually tolerated" },
  // ── Conduction disease (heart blocks / SSS) — relaxed when paced ──
  { cond: /heart block — 1st|1st degree (av |heart )?block/, re: AV_NODAL_RE, unless: /pacemaker/, severity: "warn", text: "1st-degree heart block — AV-nodal blocker may worsen conduction; recheck PR interval on ECG" },
  { cond: /mobitz|2nd degree (av |heart )?block|complete heart block|3rd degree/, re: AV_NODAL_RE, unless: /pacemaker/, severity: "block", text: "2nd/3rd-degree heart block WITHOUT pacemaker — AV-nodal blocker contraindicated (complete block/asystole risk)" },
  { cond: /sick sinus|bradycard/, re: AV_NODAL_RE, unless: /pacemaker/, severity: "block", text: "Sick sinus/bradycardia without pacemaker — rate-limiting drug contraindicated" },
  { cond: /sick sinus|bradycard/, re: /(donepezil|rivastigmine|galantamine)/, unless: /pacemaker/, severity: "warn", text: "Cholinesterase inhibitor with bradycardia — syncope/worsening bradycardia risk" },
  // ── SVT / Long QT / mechanical valve ──
  { cond: /\bsvt\b|psvt|supraventricular/, re: /(pseudoephedrine|phenylephrine)/, severity: "block", text: "Decongestant with SVT — sympathomimetic can precipitate tachyarrhythmia" },
  { cond: /\bsvt\b|psvt|supraventricular/, re: /(theophylline|salbutamol|levosalbutamol|terbutaline)/, severity: "warn", text: "Beta-agonist/theophylline with SVT — arrhythmia trigger; lowest effective dose, prefer inhaled" },
  { cond: /long qt|qt prolongation/, re: QT_RE, severity: "block", text: "Known long-QT — this drug prolongs QT further; avoid (torsades risk); if essential, cardiology guidance + ECG" },
  { cond: /mechanical valve/, re: /(rivaroxaban|apixaban|dabigatran|edoxaban)/, severity: "block", text: "DOAC with mechanical valve — contraindicated (RE-ALIGN); warfarin with INR monitoring only" },
  { cond: /mechanical valve/, re: NSAID_RE, severity: "warn", skipTopical: true, text: "NSAID with mechanical valve (on warfarin) — bleeding risk; prefer paracetamol", alt: "paracetamol" },
  // ── Respiratory: CO2 retainer, AERD, VTE ──
  { cond: /co2 retainer|type 2 resp/, re: /(alprazolam|diazepam|lorazepam|clonazepam|nitrazepam|zolpidem)/, severity: "block", text: "Sedative in CO2 retainer — respiratory depression; avoid" },
  { cond: /co2 retainer|type 2 resp/, re: /(tramadol|codeine|morphine|tapentadol|fentanyl)/, severity: "block", text: "Opioid in CO2 retainer — respiratory depression; avoid" },
  { cond: /aerd|aspirin.?sensitive|aspirin.?exacerbated/, re: new RegExp(NSAID_RE.source + "|aspirin|ecosprin"), severity: "block", skipTopical: true, text: "AERD — NSAID/aspirin triggers severe bronchospasm; paracetamol only", alt: "paracetamol" },
  { cond: /pulmonary embolism|\bdvt\b/, re: /(ethinyl|estradiol|conjugated estrogen)/, severity: "block", text: "Estrogen with prior VTE (DVT/PE) — recurrence risk; contraindicated" },
  { cond: /pulmonary embolism|\bdvt\b/, re: /tranexamic/, severity: "warn", text: "Tranexamic acid with prior VTE — thrombosis risk; avoid unless benefit clearly outweighs" },
  // ── Advanced liver disease ──
  { cond: /portal|varices/, re: NSAID_RE, severity: "block", skipTopical: true, text: "NSAID with portal HTN/varices — variceal bleed + hepatorenal risk", alt: "paracetamol" },
  { cond: /portal|varices|hepatic encephalopathy/, re: /(alprazolam|diazepam|lorazepam|clonazepam|nitrazepam|zolpidem)/, severity: "block", text: "Sedative with advanced liver disease — precipitates hepatic encephalopathy" },
  { cond: /hepatic encephalopathy/, re: /(tramadol|codeine)/, severity: "warn", text: "Opioid after hepatic encephalopathy — can precipitate recurrence; avoid or lowest dose" },
  { cond: /stricture|barrett/, re: /(doxycycline|tetracycline|alendronate|risedronate|clindamycin|potassium chloride)/, severity: "warn", text: "Pill-oesophagitis risk with stricture/Barrett's — take upright with a full glass of water; consider alternative" },
  // ── Women's health: hormonal therapy interactions ──
  { cond: /on ocp|\bhrt\b|oral contraceptive|endometriosis/, re: /(rifampicin|rifampin|carbamazepin|phenytoin|phenobarb|topiramate|st john)/, severity: "warn", text: "Enzyme inducer reduces OCP/HRT/hormonal-therapy efficacy — contraceptive failure risk; advise backup or alternative" },
  // ── Skin: SJS/TEN class caution, leprosy MDT inducer, isotretinoin ──
  { cond: /sjs|toxic epidermal|severe drug reaction/, re: /(carbamazepin|oxcarb\w*azepin|phenytoin|lamotrigin|allopurinol|cotrimoxazole|sulfamethoxazole|sulfasalazine|nevirapine)/, severity: "warn", text: "Past SJS/TEN — this drug is a classic trigger class; confirm it was NOT the culprit before prescribing" },
  { cond: /leprosy|on mdt/, re: /(warfarin|acenocoumarol|rivaroxaban|apixaban|dabigatran|itraconazole|ketoconazole|ethinyl)/, severity: "warn", text: "Rifampicin in MDT is a strong enzyme inducer — this drug's efficacy will be markedly reduced" },
  { cond: /isotretinoin/, re: /(doxycycline|tetracycline|minocycline)/, severity: "block", text: "Tetracycline with isotretinoin — intracranial hypertension; contraindicated" },
  { cond: /heart failure|hfref|hfpef/, re: /cilostazol/, severity: "block", text: "Cilostazol contraindicated in heart failure of ANY severity — increased mortality (PDE3 inhibition)" },
];

// SJS/TEN cross-reactive classes — a culprit in one member blocks the whole class
const SJS_CROSS = [
  { re: /(carbamazepin|oxcarb\w*azepin|phenytoin|phenobarb|lamotrigin|eslicarbazepin)/, label: "aromatic antiepileptic class" },
  { re: /(sulfamethoxazole|cotrimoxazole|sulfasalazine|sulfadiazine|\bsulfa\b)/, label: "sulfonamide class" },
];

// Pregnancy hard-blocks (teratogens + drugs unsafe in pregnancy/GDM)
const PREGNANCY_BLOCK_RE = /(ramipril|enalapril|lisinopril|perindopril|losartan|telmisartan|olmesartan|valsartan|atorvastatin|rosuvastatin|simvastatin|methotrexate|warfarin|acenocoumarol|doxycycline|tetracycline|minocycline|levofloxacin|ciprofloxacin|ofloxacin|moxifloxacin|isotretinoin|finasteride|valproate|mycophenolate|spironolactone|glimepiride|gliclazide|glibenclamide|glipizide|gliflozin|gliptin|pioglitazone|carbamazepin|febuxostat)/;

// QT-prolonging drugs — stacking multiple raises torsades risk

export function checkBeforeAdd(rx, ctx, opts = {}) {
  const issues = [];
  const txt = drugText(rx);
  if (!txt.trim()) return issues;
  const egfr = parseFloat(ctx.egfr);
  const age = parseFloat(ctx.age);
  const existing = (ctx.medicines || []).filter((m) => (m.brand_name || m.generic_name));

  // 1) Renal dose-guard
  if (egfr) {
    for (const r of RENAL_RULES) {
      if (!r.re.test(txt)) continue;
      if (r.skipTopical && isTopical(rx)) continue;
      if (r.block && egfr < r.block) issues.push({ severity: "block", text: `⛔ eGFR ${Math.round(egfr)}: ${r.blockText}`, alt: r.alt ? ALT[r.alt] : undefined });
      else if (r.warn && egfr < r.warn) issues.push({ severity: "warn", text: `⚠ eGFR ${Math.round(egfr)}: ${r.warnText}` });
    }
  }

  // 2) Age-guard (Beers/STOPP)
  if (age >= 65) {
    for (const r of AGE_RULES) {
      if (r.skipTopical && isTopical(rx)) continue;
      if (r.re.test(txt)) issues.push({ severity: "warn", text: `⚠ ${r.text}` });
    }
  }

  // 2b) Daily-dose ceiling — sums this ingredient across the whole table,
  // catching hidden paracetamol/NSAID inside combination brands
  issues.push(...ceilingCheck(rx, existing, ctx));

  // 2c) Pediatric mg/kg validator + child age-contraindications
  issues.push(...pediatricCheck(rx, ctx));

  // 3) Duplicate-class / cross-conflict catch — reuses the same engine that
  // gates the Save button (drugClasses.js), evaluated at tap-time.
  if (!opts.skipClassCheck) {
    const combined = [...existing, rx];
    const newIdx = combined.length - 1;
    for (const a of sameClassAlerts(combined)) {
      if (!a.rows.includes(newIdx)) continue;
      issues.push({
        severity: a.level === "critical" ? "block" : "warn",
        text: `${a.level === "critical" ? "⛔" : "⚠"} ${a.msg}`,
      });
    }
  }

  // 5) Condition-based contraindications (Known Case Of + diagnosis text)
  const knownText = (ctx.knownText || "").toLowerCase();
  if (knownText.trim()) {
    for (const r of CONDITION_RULES) {
      if (!r.cond.test(knownText) || !r.re.test(txt)) continue;
      if (r.unless && r.unless.test(knownText)) continue;
      if (r.skipTopical && isTopical(rx)) continue;
      issues.push({ severity: r.severity, text: `${r.severity === "block" ? "⛔" : "⚠"} ${r.text}`, alt: r.alt ? ALT[r.alt] : undefined });
    }
  }

  // 5b) SJS/TEN culprit drug — absolute re-exposure block + class cross-reactivity
  const culprit = (ctx.sjsCulprit || "").toLowerCase();
  if (culprit.trim()) {
    culprit.split(/[,;\n/]+/).map((t) => t.trim()).filter((t) => t.length > 3).forEach((t) => {
      if (txt.includes(t)) {
        issues.push({ severity: "block", text: `⛔ Past SJS/TEN culprit ("${t}") — NEVER re-expose; absolute contraindication` });
      } else {
        for (const g of SJS_CROSS) {
          if (g.re.test(t) && g.re.test(txt)) {
            issues.push({ severity: "block", text: `⛔ Past SJS/TEN to ${t} — cross-reactive ${g.label}; avoid re-exposure` });
          }
        }
      }
    });
  }

  // 6) Pregnancy hard-blocks
  if (ctx.pregnancy && PREGNANCY_BLOCK_RE.test(txt)) {
    const isBP = /(ramipril|enalapril|lisinopril|perindopril|losartan|telmisartan|olmesartan|valsartan)/.test(txt);
    issues.push({
      severity: "block",
      text: "⛔ Unsafe in pregnancy/GDM — use a pregnancy-safe alternative (insulin for glycaemia, labetalol/methyldopa for BP)",
      alt: isBP ? ALT.labetalol : undefined,
    });
  }
  if (ctx.pregnancy && NSAID_RE.test(txt) && !isTopical(rx)) {
    issues.push({ severity: "warn", text: "⚠ NSAID in pregnancy — avoid, especially ≥ 20 weeks (oligohydramnios/ductus closure); paracetamol preferred" });
  }
  // 6b) Pregnancy reference lookup (718-drug FDA/Briggs sheet) — widens coverage
  // beyond the curated regex without duplicating its messages
  if (ctx.pregnancy && !PREGNANCY_BLOCK_RE.test(txt)) {
    const tier = pregnancyRefLookup(txt);
    if (tier === "contraindicated") {
      issues.push({ severity: "block", text: "⛔ Contraindicated in pregnancy (FDA/Briggs reference) — use a pregnancy-safe alternative" });
    } else if (tier === "risk" && !NSAID_RE.test(txt)) {
      issues.push({ severity: "warn", text: "⚠ Positive evidence of fetal risk (FDA/Briggs) — use only if benefit clearly outweighs risk" });
    }
  }

  // 7) QT-prolonging stack
  if (QT_RE.test(txt)) {
    const qtOthers = existing.filter((m) => QT_RE.test(drugText(m)));
    if (qtOthers.length >= 2) {
      issues.push({ severity: "block", text: `⛔ QT stack: 3+ QT-prolonging drugs together (${qtOthers.map((m) => m.brand_name || m.generic_name).join(", ")} + this) — torsades risk` });
    } else if (qtOthers.length === 1) {
      issues.push({ severity: "warn", text: `⚠ QT caution: combining with ${qtOthers[0].brand_name || qtOthers[0].generic_name} — both prolong QT; check K+/Mg, consider ECG` });
    }
  }

  // 4) Allergy cross-check
  const allergyRaw = `${ctx.allergyText || ""} ${(ctx.allergyFlags || []).join(" ")}`.toLowerCase();
  if (allergyRaw.trim()) {
    for (const g of ALLERGY_GROUPS) {
      if (g.tokenRe.test(allergyRaw) && g.drugRe.test(txt)) {
        issues.push({ severity: "block", text: `⛔ ${g.label} — do not add without specialist advice` });
      }
    }
    // direct substring match on free-text allergy tokens
    allergyRaw.split(/[,;\n/]+/).map((t) => t.trim()).filter((t) => t.length > 3).forEach((t) => {
      const token = t.replace(/allergy|allergic|to|known/g, "").trim();
      if (token.length > 3 && txt.includes(token)) {
        issues.push({ severity: "block", text: `⛔ Allergy on record ("${t}") matches this drug` });
      }
    });
  }

  // dedupe
  const seen = new Set();
  return issues.filter((i) => (seen.has(i.text) ? false : (seen.add(i.text), true)));
}

// Drug → follow-up monitoring plan (appended to Next Investigations on accept).
const MONITORING_RULES = [
  { re: /(atorvastatin|rosuvastatin|simvastatin|pitavastatin)/, plan: "Lipid profile + SGPT (6 wk)" },
  { re: /carbamazepin/, plan: "CBC + LFT + Na (2 wk)" },
  { re: /(allopurinol|febuxostat)/, plan: "Uric acid + RFT (4 wk)" },
  { re: /(ramipril|enalapril|lisinopril|perindopril|telmisartan|losartan|olmesartan|valsartan|spironolactone|eplerenone)/, plan: "K+ / Creatinine (2 wk)" },
  { re: /(levothyroxine|l-thyroxine)/, plan: "TSH (6 wk)" },
  { re: /methotrexate/, plan: "CBC + LFT (4 wk)" },
  { re: /amiodarone/, plan: "TSH + LFT (3 mo)" },
  { re: /(rivaroxaban|apixaban|dabigatran)/, plan: "RFT + Hb (6 mo)" },
  { re: /(itraconazole|terbinafine)/, plan: "LFT if course > 4 wk" },
  { re: /metformin/, plan: "Vitamin B12 (yearly)" },
  { re: /gliflozin/, plan: "RFT (3 mo)" },
  { re: /(glimepiride|gliclazide|glibenclamide)/, plan: "FBS/PPBS (2-4 wk)" },
  { re: /pioglitazone/, plan: "LFT (3 mo)" },
  { re: /lithium/, plan: "Lithium level + TSH + Creatinine (3 mo)" },
];

export function monitoringPlanFor(rx) {
  const t = drugText(rx);
  if (!t.trim()) return [];
  return MONITORING_RULES.filter((r) => r.re.test(t)).map((r) => r.plan);
}

// Audit the WHOLE Rx table (covers manually-added rows) — returns alerts in
// the banner shape { level: "critical"|"caution", msg }. Duplicate-class is
// excluded here because the banner already runs sameClassAlerts.
export function auditRxTable(medicines, ctx) {
  const rows = (medicines || []).filter((m) => m.brand_name || m.generic_name);
  const alerts = [];
  rows.forEach((m) => {
    const issues = checkBeforeAdd(m, { ...ctx, medicines: [] }, { skipClassCheck: true });
    issues.forEach((i) => alerts.push({
      level: i.severity === "block" ? "critical" : "caution",
      msg: `${m.brand_name || m.generic_name}: ${i.text.replace(/^[⛔⚠]\s*/, "")}`,
    }));
  });
  // Table-wide daily-dose ceiling audit (cross-row ingredient totals)
  ceilingAudit(rows, ctx).forEach((a) => alerts.push(a));
  // Global QT-stack count across the table
  const qt = rows.filter((m) => QT_RE.test(drugText(m)));
  if (qt.length >= 2) {
    alerts.push({
      level: qt.length >= 3 ? "critical" : "caution",
      msg: `QT-prolonging stack: ${qt.map((m) => m.brand_name || m.generic_name).join(" + ")} — check K+/Mg${qt.length >= 3 ? ", ECG strongly advised" : ""}`,
    });
  }
  const seen = new Set();
  return alerts.filter((a) => (seen.has(a.msg) ? false : (seen.add(a.msg), true)));
}

// Renal-adaptive dosing — rewrites the card's dose from the patient's eGFR
// BEFORE display/add, so the suggestion is pre-corrected, not just guarded.
const RENAL_ADJUST = [
  { re: /metformin/, band: [30, 45], dose: "500 mg", freq: "BD", note: "max 1000 mg/day (eGFR 30-44)" },
  { re: /sitagliptin/, band: [30, 45], dose: "50 mg", note: "50 mg OD at eGFR 30-45" },
  { re: /sitagliptin/, band: [0, 30], dose: "25 mg", note: "25 mg OD at eGFR < 30" },
  { re: /allopurinol/, band: [0, 60], dose: "50-100 mg", note: "CKD: start low, titrate by uric acid" },
  { re: /tramadol/, band: [0, 30], freq: "BD (q12h)", note: "q12h, max 200 mg/day at eGFR < 30" },
  { re: /(gliclazide|glimepiride)/, band: [0, 45], note: "CKD: use lowest dose — hypoglycaemia risk" },
  { re: /pregabalin/, band: [0, 60], dose: "75 mg", freq: "OD", note: "reduce pregabalin dose in CKD" },
  { re: /gabapentin/, band: [0, 60], note: "reduce gabapentin dose in CKD" },
  { re: /levofloxacin/, band: [0, 50], note: "eGFR < 50: dose-reduce / extend interval" },
];

export function adjustRxForContext(rx, ctx) {
  const egfr = parseFloat(ctx?.egfr);
  if (!egfr || !rx) return { rx, note: null };
  const t = drugText(rx);
  for (const r of RENAL_ADJUST) {
    if (!r.re.test(t)) continue;
    if (egfr >= r.band[0] && egfr < r.band[1]) {
      const adj = { ...rx };
      if (r.dose) adj.dose = r.dose;
      if (r.freq) adj.frequency = r.freq;
      adj.instructions = [adj.instructions, `Renal-adjusted (eGFR ${Math.round(egfr)}): ${r.note}`].filter(Boolean).join(" · ");
      return { rx: adj, note: `eGFR ${Math.round(egfr)} → ${r.note}` };
    }
  }
  return { rx, note: null };
}

// Auto-quantity: tablets/capsules per day × days (card duration, else follow-up).
const FREQ_PER_DAY = { od: 1, hs: 1, bd: 2, "bd (q12h)": 2, tds: 3, tid: 3, qid: 4, "once weekly": 1 / 7, weekly: 1 / 7 };
export function computeQuantity(rx, followUpText) {
  const form = `${rx.dosage_form || ""}`.toLowerCase();
  if (form && !/tab|cap/.test(form)) return null;
  const doseM = String(rx.dose || "").trim().match(/^(\d+(?:\.\d+)?)\s*(tab|cap)?$/i);
  const perDose = doseM ? parseFloat(doseM[1]) : (/^(\d+(?:\.\d+)?)\s*(tab|cap)/i.test(String(rx.dose || "")) ? parseFloat(rx.dose) : null);
  if (!perDose || perDose > 4) return null;
  const freqKey = String(rx.frequency || "").trim().toLowerCase();
  let perDay = FREQ_PER_DAY[freqKey];
  if (!perDay) {
    if (/prn|sos|stat/i.test(freqKey)) return null;
    if (/^od\b/.test(freqKey)) perDay = 1;
    else if (/^bd\b/.test(freqKey)) perDay = 2;
    else if (/^tds\b/.test(freqKey)) perDay = 3;
    else if (/weekly/.test(freqKey)) perDay = 1 / 7;
    else return null;
  }
  const durSrc = String(rx.duration || "").toLowerCase();
  let dm = durSrc.match(/(\d+)(?:\s*-\s*(\d+))?\s*(day|week|month)/);
  if (!dm) dm = String(followUpText || "").toLowerCase().match(/(\d+)(?:\s*-\s*(\d+))?\s*(day|week|month)/);
  if (!dm) return null;
  const n = parseInt(dm[2] || dm[1], 10);
  const days = dm[3] === "week" ? n * 7 : dm[3] === "month" ? n * 30 : n;
  if (!days || days > 120) return null;
  const qty = Math.ceil(perDose * perDay * days);
  if (!qty || qty < 1) return null;
  return `${qty} ${/cap/.test(form) ? "caps" : "tabs"}`;
}

// #3 card upgrade: titration ladders for "already on it" quick up/down actions.
const TITRATION_LADDERS = [
  { re: /atorvastatin/, ladder: [10, 20, 40, 80] },
  { re: /rosuvastatin/, ladder: [5, 10, 20, 40] },
  { re: /telmisartan/, ladder: [20, 40, 80] },
  { re: /losartan/, ladder: [25, 50, 100] },
  { re: /amlodipine/, ladder: [2.5, 5, 10] },
  { re: /glimepiride/, ladder: [1, 2, 3, 4] },
  { re: /gliclazide/, ladder: [30, 60, 90, 120] },
  { re: /(levothyroxine|l-thyroxine|thyroxine)/, ladder: [25, 50, 75, 100, 125, 150] },
  { re: /bisoprolol/, ladder: [2.5, 5, 10] },
  { re: /metoprolol/, ladder: [25, 50, 100] },
  { re: /pregabalin/, ladder: [75, 150, 300] },
  { re: /gabapentin/, ladder: [100, 300, 600] },
  { re: /escitalopram/, ladder: [5, 10, 20] },
  { re: /sertraline/, ladder: [25, 50, 100] },
  { re: /betahistine/, ladder: [8, 16, 24] },
  { re: /allopurinol/, ladder: [100, 200, 300] },
  { re: /empagliflozin/, ladder: [10, 25] },
  { re: /dapagliflozin/, ladder: [5, 10] },
];

export function titrationOptions(m) {
  const t = drugText(m);
  if (!t.trim()) return null;
  const rule = TITRATION_LADDERS.find((r) => r.re.test(t));
  if (!rule) return null;
  const cur = parseFloat(String(m.strength || m.dose || "").match(/(\d+(?:\.\d+)?)/)?.[1]);
  if (!cur) return null;
  const idx = rule.ladder.findIndex((v) => Math.abs(v - cur) < 0.01);
  if (idx === -1) return null;
  return {
    current: cur,
    up: idx < rule.ladder.length - 1 ? rule.ladder[idx + 1] : null,
    down: idx > 0 ? rule.ladder[idx - 1] : null,
  };
}

export function retitrateRow(m, newStrength) {
  const opts = titrationOptions(m);
  const cur = opts?.current;
  const next = { ...m, strength: `${newStrength} mg` };
  if (cur && m.brand_name) next.brand_name = m.brand_name.replace(new RegExp(`\\b${cur}\\b`), String(newStrength));
  if (cur && m.composition) next.composition = m.composition.replace(new RegExp(`\\b${cur}\\s*mg\\b`), `${newStrength} mg`);
  next.instructions = [m.instructions, `Dose changed: ${cur} → ${newStrength} mg this visit`].filter(Boolean).join(" · ");
  return next;
}

// #7 card upgrade: remember the doctor's own dose/frequency style per generic.
const PREF_KEY = "card_dose_prefs_v1";
export function loadDosePrefs() {
  try { return JSON.parse(localStorage.getItem(PREF_KEY) || "{}"); } catch { return {}; }
}
export function applyDosePref(rx) {
  const key = (rx.generic_name || "").toLowerCase().trim();
  if (!key) return { rx, applied: null };
  const p = loadDosePrefs()[key];
  if (!p) return { rx, applied: null };
  if ((p.dose && p.dose !== rx.dose) || (p.frequency && p.frequency !== rx.frequency)) {
    return {
      rx: { ...rx, dose: p.dose || rx.dose, frequency: p.frequency || rx.frequency },
      applied: `${p.dose || rx.dose} ${p.frequency || rx.frequency}`,
    };
  }
  return { rx, applied: null };
}
export function recordDosePrefs(medicines) {
  try {
    const prefs = loadDosePrefs();
    let changed = false;
    (medicines || []).forEach((m) => {
      if (!m._cardDose || !m.generic_name) return;
      if (m.dose !== m._cardDose || m.frequency !== m._cardFreq) {
        prefs[m.generic_name.toLowerCase().trim()] = { dose: m.dose, frequency: m.frequency };
        changed = true;
      }
    });
    if (changed) localStorage.setItem(PREF_KEY, JSON.stringify(prefs));
  } catch (e) { console.debug("[silent]", e); }
}
